import { FC, useState, useRef, memo } from 'react';
import Axios from '~/utils/axios';
import { Translate, useTranslate } from '~/i18n';
import { MyVoucher } from '~/interfaces/vouchers';
import Loading from '~/components/common/loaders/loader';
import { CloseIconLight } from '~/svgs/common';
import dynamic from 'next/dynamic';
import cn from 'classnames';
import {
   profileVouchersTabRedeemBtnEvent,
   profileVouchersTabApplyCodeBtnEvent,
   profileVouchersTabApplyCodeBtnFailedEvent,
   profileVouchersTabApplyCodeBtnSuccessEvent,
} from '~/utils/mixpanel-events/profile';
const RedeemedModal = dynamic(() => import('./redeemed-modal'), { ssr: false });

type VoucherBalanceProps = {
   userBalance?: number;
   // eslint-disable-next-line no-unused-vars
   updateVouchersData: (param: any) => void;
};

/**
 * This component is responsoble for keeping track of user voucher credit
 * Note: it does not manage the balance state, it gets it from the parent
 */
const VoucherBalance: FC<VoucherBalanceProps> = ({ userBalance: balance, updateVouchersData }) => {
   const { translate } = useTranslate();
   const [redeemFormOpen, setRedeemFormOpen] = useState(false);
   const [voucherInput, setVoucher] = useState('');
   const [loading, setLoading] = useState(false);
   const [redeemedVoucher, setRedeemedVoucher] = useState<MyVoucher>();
   const [redeeming, setRedeem] = useState(false);
   const [errorCode, setErrorCode] = useState('');
   const error = useRef(false);

   const handleVoucherSubmit = async (e: React.FormEvent<HTMLFormElement>) => {
      e.preventDefault();
      profileVouchersTabApplyCodeBtnEvent();
      setLoading(true);
      try {
         const { data } = await Axios.post('/vouchers/redeem', {
            code: voucherInput,
            with_history: true,
         });

         if (data.status === 'Failed') {
            setErrorCode(data.errors.code[0]);
            profileVouchersTabApplyCodeBtnFailedEvent(data.errors.code[0]);
            error.current = true;
         } else {
            // Success
            // There should be one & only one item in the redeemed vouchers that
            // matches the code, otherwise there is something weird happening
            const myVoucher = data.data.redeemed_vouchers.filter(
               (voucher: MyVoucher) => voucher.code === voucherInput
            );
            updateVouchersData({
               ...data.data,
               redeemed: myVoucher[0],
               balance_history: data.data.history.data,
            });
            setRedeemedVoucher(myVoucher[0]);
            setVoucher('');
            profileVouchersTabApplyCodeBtnSuccessEvent();
         }
      } catch (error: any) {
         console.error(error);
         profileVouchersTabApplyCodeBtnFailedEvent(error.message);
      }
      setLoading(false);
      handleRedeem();
   };

   // Handles bottom sheet and modal open on voucher redemption success
   const handleRedeem = () => {
      if (!error.current) {
         setRedeem(true);
      } else {
         setRedeem(false);
      }
   };

   const handleClose = () => setRedeem(false);

   return (
      <div
         className="flex flex-col text-white bg-no-repeat bg-primary-blue rounded-2xl"
         style={{
            backgroundImage: 'url(/images/vouchers/waves.svg)',
            backgroundSize: 'cover',
         }}>
         {/* Redeem Voucher Button */}
         <div className="mt-2 ltr:mr-2 rtl:ml-2 ltr:text-right rtl:text-left">
            <button
               className="mb-1"
               onClick={() => {
                  setRedeemFormOpen(prev => !prev);
                  !redeemFormOpen && profileVouchersTabRedeemBtnEvent();
               }}>
               {redeemFormOpen ? (
                  <Translate id="vouchers:cancel" />
               ) : (
                  <div>
                     {'+ '}
                     <Translate id="vouchers:redeem voucher" />
                  </div>
               )}
            </button>
         </div>
         {/* Balance */}
         <div className="mb-1 text-sm ltr:ml-4 rtl:mr-4">
            <Translate id="vouchers:balance" />
         </div>
         <div className="text-2xl font-semibold ltr:ml-4 rtl:mr-4">
            {/* Add translation and get value from backend */}
            {balance}
            <Translate id="vouchers:sar" />
         </div>
         {/* Apply Voucher Form */}
         <div className="mb-4 mt-2.5 mx-4">
            <form className={cn(!redeemFormOpen && 'hidden')} onSubmit={handleVoucherSubmit}>
               <div className="flex items-center bg-white rounded-lg">
                  {!errorCode ? (
                     <input
                        // assuming code is always from english alphabet
                        // dir="ltr"
                        className="flex-grow text-xs text-black bg-transparent border-none focus:ring-0 small:text-base"
                        size={10}
                        value={voucherInput}
                        onChange={e => setVoucher(e.currentTarget.value)}
                        type="text"
                        placeholder={translate({ id: 'vouchers:enter code' })}
                     />
                  ) : (
                     // Voucher code error
                     <div className="flex items-center flex-grow">
                        <p className="flex-grow px-3 leading-none bg-transparent border-none focus:ring-0 text-primary-error">
                           {voucherInput}
                           <br />
                           <span className="text-sm">{errorCode}</span>
                        </p>
                        <CloseIconLight
                           className="p-px rounded-full cursor-pointer bg-primary-disabled focus:outline-none focus:shadow-md ltr:mr-2 rtl:ml-2"
                           onClick={() => {
                              setVoucher('');
                              setErrorCode('');
                              error.current = false;
                           }}
                        />
                     </div>
                  )}
                  <button
                     disabled={!voucherInput || loading || error.current}
                     className="w-16 p-2 my-2 text-xs font-semibold rounded-md bg-primary-green bg-opacity-10 disabled:opacity-60 focus:outline-none ltr:mr-2 rtl:ml-2 text-primary-green">
                     {loading ? (
                        <Loading color="#00983F" size={16} />
                     ) : (
                        <Translate id="common:apply" />
                     )}
                  </button>
               </div>
            </form>
         </div>
         {/* Apply Voucher Success */}
         <RedeemedModal
            handleClose={handleClose}
            redeemedVoucher={redeemedVoucher}
            redeeming={redeeming}
         />
      </div>
   );
};

export default memo(VoucherBalance);
